<script setup lang="ts">
import { DateTime } from 'luxon';
import type { CommonFooterProperties } from '@optionway/models';

const properties = defineProps<CommonFooterProperties>();

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.commonFooter;
const { isCollapsed, isFunnelPage } = useFunnelRoute();
const { locale } = useI18n();

const componentConfig = computed<CommonFooterProperties>(() => {
  return {
    components: properties.components ?? config?.components,
    hideComponents: config?.hideComponents,
  };
});

const currentYear = DateTime.now().year;
</script>

<template>
  <footer
    id="footer"
    class="flex flex-col items-center gap-8 border-t border-gray-300 bg-background px-4 py-6 md:px-8 md:pb-8 md:pt-8"
    :class="isFunnelPage && 'pb-drawer-collapsed md:pb-drawer-collapsed xl:pb-8'"
  >
    <UiCustomContainer class="w-full gap-6">
      <template v-if="!componentConfig.hideComponents?.commonFooterCollapsed">
        <CommonFooterCollapsed v-if="isCollapsed"
      /></template>
      <template v-if="!componentConfig.hideComponents?.commonFooterFull">
        <!-- CommonFooterFull builds its links once at setup: remount on locale change so the help URL and title follow -->
        <CommonFooterFull
          v-if="!isCollapsed"
          :key="locale"
          :help-url="componentConfig.components?.commonFooterFull?.helpUrl"
      /></template>

      <div
        v-if="!isCollapsed"
        class="flex w-full flex-col items-center gap-4 border-t border-gray-300 pt-4"
      >
        <div class="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
          <UiCustomResponsivePicture
            src="common/footer/accreditations/iata.png"
            :alt="$t('components.common.footer.accreditations.iata')"
            sizes="107px"
            width="152"
            height="80"
            :img-attrs="{ class: 'h-14 w-auto grayscale' }"
          />
          <UiCustomResponsivePicture
            src="common/footer/accreditations/atout-france.png"
            :alt="$t('components.common.footer.accreditations.atoutFrance')"
            sizes="107px"
            width="152"
            height="80"
            :img-attrs="{ class: 'h-14 w-auto grayscale' }"
          />
          <UiCustomResponsivePicture
            src="common/footer/accreditations/apst.png"
            :alt="$t('components.common.footer.accreditations.apst')"
            sizes="107px"
            width="152"
            height="80"
            :img-attrs="{ class: 'h-14 w-auto grayscale' }"
          />
          <UiCustomResponsivePicture
            src="common/footer/accreditations/edv.png"
            :alt="$t('components.common.footer.accreditations.edv')"
            sizes="107px"
            width="152"
            height="80"
            :img-attrs="{ class: 'h-14 w-auto grayscale' }"
          />
        </div>
        <p class="text-center text-xs font-normal text-text-secondary-500">
          © {{ currentYear }} Option Way. {{ $t('components.common.footer.allRightsReserved') }}
        </p>
      </div>
    </UiCustomContainer>
  </footer>
</template>
